import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { 
  Cookie, X, Settings, Shield, BarChart3, Target, ChevronDown, ChevronUp 
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const COOKIE_CONSENT_KEY = "skilledge_cookie_consent";

const CookieConsentBanner = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState({
    essential: true, // Always required
    functional: true,
    analytics: false,
    marketing: false
  });

  useEffect(() => {
    // Check if user has already made a choice
    const savedConsent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!savedConsent) {
      // Show banner after a short delay
      const timer = setTimeout(() => setIsVisible(true), 1500);
      return () => clearTimeout(timer);
    } else {
      // Load saved preferences
      try {
        const parsed = JSON.parse(savedConsent);
        setPreferences(prev => ({ ...prev, ...parsed.preferences }));
      } catch (e) {
        console.error("Error parsing cookie consent:", e);
      }
    }
  }, []);

  const saveConsent = (prefs) => {
    const consent = {
      timestamp: new Date().toISOString(),
      preferences: prefs
    };
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(consent));
    setIsVisible(false);

    // Dispatch event for other components to react
    window.dispatchEvent(new CustomEvent('cookieConsentUpdated', { detail: prefs }));
  };

  const acceptAll = () => {
    const allAccepted = {
      essential: true,
      functional: true,
      analytics: true,
      marketing: true
    };
    setPreferences(allAccepted);
    saveConsent(allAccepted);
  };

  const acceptEssentialOnly = () => {
    const essentialOnly = {
      essential: true,
      functional: false,
      analytics: false,
      marketing: false
    };
    setPreferences(essentialOnly);
    saveConsent(essentialOnly);
  };

  const savePreferences = () => {
    saveConsent(preferences);
  };

  const cookieCategories = [
    {
      id: "essential",
      name: "Essential",
      icon: Shield,
      required: true,
      description: "Required for basic functionality. Cannot be disabled.",
      color: "text-blue-600"
    },
    {
      id: "functional",
      name: "Functional",
      icon: Settings,
      required: false,
      description: "Remember preferences and provide enhanced features.",
      color: "text-purple-600"
    },
    {
      id: "analytics",
      name: "Analytics",
      icon: BarChart3,
      required: false,
      description: "Help us understand how you use our website.",
      color: "text-green-600"
    },
    {
      id: "marketing",
      name: "Marketing",
      icon: Target,
      required: false,
      description: "Used to deliver relevant advertisements.",
      color: "text-orange-600"
    }
  ];

  if (!isVisible) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 100, opacity: 0 }}
        transition={{ type: "spring", damping: 25, stiffness: 300 }}
        className="fixed bottom-0 left-0 right-0 z-50 p-4"
        data-testid="cookie-consent-banner"
      >
        <Card className="max-w-4xl mx-auto shadow-2xl border-0 bg-white/95 backdrop-blur-lg">
          <CardContent className="p-6">
            {/* Header */}
            <div className="flex items-start justify-between gap-4 mb-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-gradient-to-br from-orange-400 to-amber-500 rounded-xl flex items-center justify-center">
                  <Cookie className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">Cookie Preferences</h3>
                  <p className="text-sm text-gray-600">
                    We use cookies to enhance your experience
                  </p>
                </div>
              </div>
              <button 
                onClick={acceptEssentialOnly}
                className="text-gray-400 hover:text-gray-600 transition"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Main message */}
            <p className="text-sm text-gray-600 mb-4">
              We use cookies and similar technologies to provide you with a better experience, 
              analyze traffic, and personalize content. Read our{" "}
              <Link to="/cookies" className="text-blue-600 hover:underline">Cookie Policy</Link>
              {" "}for more information.
            </p>

            {/* Expandable details */}
            <div className="mb-4">
              <button
                onClick={() => setShowDetails(!showDetails)}
                className="flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-gray-900 transition"
              >
                {showDetails ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                {showDetails ? "Hide" : "Show"} cookie settings
              </button>

              <AnimatePresence>
                {showDetails && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="overflow-hidden"
                  >
                    <div className="grid md:grid-cols-2 gap-3 mt-4">
                      {cookieCategories.map((category) => {
                        const IconComponent = category.icon;
                        return (
                          <div 
                            key={category.id}
                            className={`p-3 rounded-lg border ${
                              preferences[category.id] 
                                ? 'bg-gray-50 border-gray-200' 
                                : 'bg-gray-50/50 border-gray-100'
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <IconComponent className={`w-4 h-4 ${category.color}`} />
                                <span className="font-medium text-sm text-gray-900">
                                  {category.name}
                                </span>
                                {category.required && (
                                  <span className="text-xs text-gray-500">(Required)</span>
                                )}
                              </div>
                              <Switch
                                checked={preferences[category.id]}
                                onCheckedChange={(checked) => {
                                  if (!category.required) {
                                    setPreferences(prev => ({
                                      ...prev,
                                      [category.id]: checked
                                    }));
                                  }
                                }}
                                disabled={category.required}
                              />
                            </div>
                            <p className="text-xs text-gray-500 mt-1">
                              {category.description}
                            </p>
                          </div>
                        );
                      })}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Action buttons */}
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                onClick={acceptEssentialOnly}
                variant="outline"
                className="flex-1 sm:flex-none"
              >
                Essential Only
              </Button>
              {showDetails && (
                <Button
                  onClick={savePreferences}
                  variant="outline"
                  className="flex-1 sm:flex-none"
                >
                  Save Preferences
                </Button>
              )}
              <Button
                onClick={acceptAll}
                className="flex-1 sm:flex-none bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white"
              >
                Accept All
              </Button>
            </div>

            {/* Links */}
            <div className="flex flex-wrap gap-4 mt-4 text-xs text-gray-500">
              <Link to="/privacy" className="hover:text-gray-700 hover:underline">
                Privacy Policy
              </Link>
              <Link to="/cookies" className="hover:text-gray-700 hover:underline">
                Cookie Policy
              </Link>
              <Link to="/terms" className="hover:text-gray-700 hover:underline">
                Terms of Service
              </Link>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </AnimatePresence>
  );
};

// Hook to check cookie preferences
export const useCookieConsent = () => {
  const [consent, setConsent] = useState(null);

  useEffect(() => {
    const loadConsent = () => {
      const saved = localStorage.getItem(COOKIE_CONSENT_KEY);
      if (saved) {
        try {
          setConsent(JSON.parse(saved));
        } catch (e) {
          console.error("Error parsing cookie consent:", e);
        }
      }
    };

    loadConsent();

    // Listen for updates
    const handleUpdate = (e) => {
      setConsent({ preferences: e.detail, timestamp: new Date().toISOString() });
    };
    window.addEventListener('cookieConsentUpdated', handleUpdate);
    
    return () => window.removeEventListener('cookieConsentUpdated', handleUpdate);
  }, []);

  return consent;
};

// Function to reset cookie consent (for testing or user request)
export const resetCookieConsent = () => {
  localStorage.removeItem(COOKIE_CONSENT_KEY);
  window.location.reload();
};

export default CookieConsentBanner;
